import { useUnit } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { createFlexibleBlock } from '@nx-frontend/shared/blocks/flexible';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import { $menuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { $pageBlocks } from '@nx-frontend/shared/store/portal-blocks';
import {
  FlexibleBlock,
  isPageMenuItemType,
  MenuItemChangeTypePayload,
  MenuItemType,
  MenuType,
  PortalBlockType,
  PortalProcessItem,
  SectionMenuItem,
} from '@nx-frontend/shared/types';
import {
  ChangeEventBase,
  ChangeEventType,
  ChangeEventWithPartialMeta,
  DeleteBlockEvent,
} from '@nx-frontend/shared/types';
import { calcProcessData } from '@nx-frontend/shared/utils';

type AllowedMenuTypes = MenuItemType.PORTAL_SECTION | MenuItemType.PORTAL_PROCESS;

export interface ChangeMenuItemTypePayload<T extends AllowedMenuTypes = AllowedMenuTypes>
  extends Pick<ChangeEventBase, 'squashId' | 'collectId' | 'batchId'> {
  menuType: MenuType;
  item: SectionMenuItem;
  newType: T;
}
export const useChangeMenuItemType = () => {
  const [pageBlocks, menuState] = useUnit([$pageBlocks, $menuState]);
  const setChanges = useChanges();
  const { toast } = useToast();

  const getPortalProcessChanges = async ({
    item,
    menuType,
    newType,
  }: ChangeMenuItemTypePayload<MenuItemType.PORTAL_PROCESS>): Promise<ChangeEventWithPartialMeta[]> => {
    const menuItems = Object.values(menuState[menuType].entities);
    const currentItem = menuItems.find(({ id }) => id === item.id);
    const hasRestrictedItems: string[] = [];
    const items = currentItem
      ? menuItems.reduce<PortalProcessItem[]>((acc, item) => {
          if (currentItem.order?.includes(item.id)) {
            if (!isPageMenuItemType(item)) {
              hasRestrictedItems.push(item.name);
            }
            acc.push({
              id: item.id,
              visible: item.visible,
              status: 'todo',
            });
          }
          return acc;
        }, [])
      : [];

    if (hasRestrictedItems.length) {
      toast({
        title: `Cannot convert folder because it contains non-page items: ${hasRestrictedItems.join(', ')}`,
      });
      return [];
    }

    const newItem: MenuItemChangeTypePayload['item'] = {
      ...item,
      type: newType,
      icon: 'process',
      process: calcProcessData({
        status: 'todo',
        sequential: false,
        items,
      }),
    };

    const events: ChangeEventWithPartialMeta[] = [];
    events.push(
      {
        type: ChangeEventType.CHANGE_MENU_ITEM_TYPE,
        oldValue: { menuType, item },
        newValue: { menuType, item: newItem },
      },
      {
        type: ChangeEventType.ADD_BLOCK,
        oldValue: undefined,
        newValue: createFlexibleBlock({
          template: { type: 'process-state', view: 'basic' },
          index: -2,
          removable: false,
          pageId: item.pageId ?? '',
          modifier: { bricks: [{ brickType: 'process-state', relatedMenuItemId: item.id }] },
        }),
      }
    );

    events.push({
      type: ChangeEventType.ADD_BLOCK,
      oldValue: undefined,
      newValue: createFlexibleBlock({
        template: { type: 'process-action', view: 'basic' },
        index: 1001,
        removable: false,
        pageId: item.pageId ?? '',
        modifier: { bricks: [{ brickType: 'process-action', relatedMenuItemId: item.id }] },
      }),
    });
    return events;
  };

  const getPortalSectionChanges = async ({
    menuType,
    item,
    newType,
  }: ChangeMenuItemTypePayload<MenuItemType.PORTAL_SECTION>): Promise<ChangeEventWithPartialMeta[]> => {
    const blocksToRemove = pageBlocks.filter(
      (block) =>
        block.ghost !== true &&
        block.type === PortalBlockType.FLEXIBLE &&
        ['process-state', 'process-action'].includes(block.template.type)
    ) as FlexibleBlock[];

    const blockDeleteEvents = blocksToRemove.map<DeleteBlockEvent>((block) => ({
      type: ChangeEventType.DELETE_BLOCK,
      oldValue: block,
      newValue: undefined,
    }));

    return [
      ...blockDeleteEvents,
      {
        type: ChangeEventType.CHANGE_MENU_ITEM_TYPE,
        oldValue: { menuType, item },
        newValue: {
          menuType,
          item: {
            ...item,
            type: newType,
            icon: 'folder',
          },
        },
      },
    ];
  };

  return async ({
    menuType,
    item,
    newType,
    squashId,
    collectId,
    batchId = generateUuid(),
  }: ChangeMenuItemTypePayload): Promise<boolean> => {
    const changes = await (newType === MenuItemType.PORTAL_PROCESS
      ? getPortalProcessChanges({ menuType, item, newType })
      : getPortalSectionChanges({ menuType, item, newType }));

    for (const change of changes) {
      await setChanges({ ...change, squashId, collectId, batchId });
    }
    return changes.length > 0;
  };
};
